import React, { useState } from "react";
import axiosInstance from "../../../../components/axiosInstance";
import { API_URLS } from "../../../../apiUrl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

type ViewType = "list" | "form" | "csv";

interface Props {
  setView: React.Dispatch<React.SetStateAction<ViewType>>;
  streetId: string | number;
  municipalityId : string | number;
}

interface ErrorRow {
  lineNumber: number;
  rawData: string;
  reason: string;
}

const ConsumerCSV: React.FC<Props> = ({ setView, streetId, municipalityId }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const parseCSV = (text: string): {
    valid: Record<string, string>[];
    errors: ErrorRow[];
    headers: string[];
  } => {
    const rows = text
      .split("\n")
      .map((row) => row.trim())
      .filter((row) => row.length > 0);

    const headers = rows[0].split(",").map((h) => h.trim());
    const valid: Record<string, string>[] = [];
    const errors: ErrorRow[] = [];

    rows.slice(1).forEach((row, index) => {
      const lineNumber = index + 2;
      const values = row.split(",");

      if (values.length !== headers.length) {
        errors.push({
          lineNumber,
          rawData: row,
          reason: `Expected ${headers.length} columns but got ${values.length}`,
        });
        return;
      }

      const obj: Record<string, string> = {};
      headers.forEach((header, i) => {
        obj[header] = values[i]?.trim() ?? "";
      });
      valid.push(obj);
    });

    return { valid, errors, headers };
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      toast.error("Please select a CSV file");
      return;
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const { valid, errors, headers } = parseCSV(text);

      // ✅ CSV parse errors toast এ দেখাও
      errors.forEach((err) => {
        toast.error(`Line #${err.lineNumber}: ${err.reason}`);
      });

      if (valid.length === 0) {
        toast.error("No valid rows found to upload.");
        return;
      }

      const csvContent = [
        headers.join(","),
        ...valid.map((row) => headers.map((key) => row[key] ?? "").join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const csvFile = new File([blob], "valid_rows.csv", { type: "text/csv" });

      const formData = new FormData();
      formData.append("csv_file", csvFile);
      formData.append("street_id", String(streetId));
      formData.append("municipality_id", String(municipalityId));

      setUploading(true);
      try {
        await axiosInstance.post(API_URLS.uploadcsv, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("CSV uploaded successfully!");
        setView("list");
      } catch (err: any) {
        const message = err?.response?.data?.message || "Upload failed. Please try again.";
        toast.error(message);
      } finally {
        setUploading(false);
      }
    };

    reader.readAsText(file);
  };

  return (
    <>
      {/* ─── Small Popup Modal ─── */}
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
        <div className="bg-white w-[460px] rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-gray-800">Import Consumer CSV</h2>
            <button
              onClick={() => setView("list")}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleUpload} className="space-y-4">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="w-full border border-gray-300 px-3 py-2 rounded text-sm text-gray-600"
            />

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setView("list")}
                className="bg-gray-100 text-gray-600 px-4 py-2 rounded text-sm hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="bg-green-600 text-white px-5 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
              >
                {uploading ? "Uploading..." : "Upload CSV"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ─── Loader Overlay ─── */}
      {uploading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-60 z-[60]">
          <div className="bg-white rounded-xl px-10 py-8 flex flex-col items-center gap-4 shadow-xl">
            <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-700 font-medium">
              Please wait while CSV is being uploaded...
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsumerCSV;